const NODES = [
  { key: "spirit", label: "Дух и род" },
  { key: "realization", label: "Реализация" },
  { key: "relations", label: "Отношения" },
  { key: "health", label: "Здоровье" },
  { key: "personality", label: "Личность" },
] as const;

export type StarKey = (typeof NODES)[number]["key"];

const C = 150;
const R = 92;

function point(i: number, radius = R): { x: number; y: number } {
  const a = (-90 + i * 72) * (Math.PI / 180);
  return { x: C + radius * Math.cos(a), y: C + radius * Math.sin(a) };
}

/** Пентаграмма «звезды»: пять чисел, по нажатию выбирается сфера. */
export function StarDiagram({
  numbers,
  active,
  onPick,
  labels = true,
}: {
  numbers: Record<string, number | number[]>;
  active: StarKey | null;
  onPick?: (k: StarKey) => void;
  labels?: boolean;
}) {
  const pts = NODES.map((_, i) => point(i));
  const edges = [0, 1, 2, 3, 4].map((i) => [pts[i]!, pts[(i + 2) % 5]!] as const);
  return (
    <svg className="star" viewBox="0 0 300 300" role="img" aria-label="Звезда личности">
      <circle className="ring" cx={C} cy={C} r={R + 20} />
      {edges.map(([a, b], i) => (
        <line key={i} className="edge" x1={a.x} y1={a.y} x2={b.x} y2={b.y} />
      ))}
      {NODES.map((n, i) => {
        const p = pts[i]!;
        const v = numbers[n.key];
        const label = pointLabel(i);
        return (
          <g
            key={n.key}
            className={`node${active === n.key ? " active" : ""}`}
            onClick={() => onPick?.(n.key)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && onPick?.(n.key)}
            style={{ cursor: onPick ? "pointer" : "default" }}
          >
            <circle cx={p.x} cy={p.y} r={22} />
            <text x={p.x} y={p.y + 1}>
              {typeof v === "number" ? v : "·"}
            </text>
            {labels && (
              <text className="cap" x={label.x} y={label.y}>
                {n.label}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function pointLabel(i: number): { x: number; y: number } {
  const p = point(i);
  return { x: p.x, y: i === 0 ? p.y - 36 : p.y + 38 };
}

/** Треугольник из одного главного и нескольких числовых узлов (детский профиль, денежный код). */
export function Trio({ top, base }: { top: number | string; base: (number | string)[] }) {
  if (base.length === 0) {
    return (
      <svg className="trio" viewBox="0 0 300 120" role="img" aria-label="Главное число">
        <circle className="top" cx={150} cy={60} r={46} />
        <text x={150} y={61} style={{ fontSize: 38 }}>
          {top}
        </text>
      </svg>
    );
  }
  const pts = base.length === 2 ? [[70, 190], [230, 190]] : [[50, 190], [150, 190], [250, 190]];
  return (
    <svg className="trio" viewBox="0 0 300 230" role="img" aria-label="Числа разбора">
      <path className="edge" d={`M150 40 L${pts[0]![0]} ${pts[0]![1]} L${pts[pts.length - 1]![0]} ${pts[pts.length - 1]![1]} Z`} />
      <circle className="top" cx={150} cy={40} r={28} />
      <text x={150} y={41}>
        {top}
      </text>
      {base.map((b, i) => (
        <g key={i}>
          <circle cx={pts[i]![0]} cy={pts[i]![1]} r={26} />
          <text x={pts[i]![0]} y={pts[i]![1]! + 1}>
            {b}
          </text>
        </g>
      ))}
    </svg>
  );
}

/**
 * Родовая схема: слева папа, справа мама (прямой ответ автора, Р-24), внизу главный урок.
 * Числа — родовые точки звезды: у мамы P2+P3 и P5+P1, у папы P1+P2 и P3+P4; главный урок — P4+P5.
 */
export function KinDiagram({ mother, father, lesson }: { mother: [number, number]; father: [number, number]; lesson: number }) {
  const me = { x: 150, y: 70 };
  const nodes = [
    { x: 55, y: 60, v: father[0], cap: "мужская линия" },
    { x: 55, y: 140, v: father[1], cap: "женская линия" },
    { x: 245, y: 60, v: mother[0], cap: "мужская линия" },
    { x: 245, y: 140, v: mother[1], cap: "женская линия" },
  ];
  return (
    <svg className="kin" viewBox="0 0 300 270" role="img" aria-label="Родовая схема: папа слева, мама справа">
      <text className="who" x={55} y={16}>ПАПА</text>
      <text className="who" x={245} y={16}>МАМА</text>
      {nodes.map((n, i) => (
        <line key={`l${i}`} className="edge" x1={me.x} y1={me.y + 20} x2={n.x} y2={n.y} />
      ))}
      <line className="edge" x1={me.x} y1={me.y + 34} x2={150} y2={196} />
      <circle className="me" cx={me.x} cy={me.y + 20} r={30} />
      <text x={me.x} y={me.y + 21}>Вы</text>
      {nodes.map((n, i) => (
        <g key={i}>
          <circle cx={n.x} cy={n.y} r={26} />
          <text x={n.x} y={n.y + 1}>{n.v}</text>
          <text className="cap" x={n.x} y={n.y + 44}>{n.cap}</text>
        </g>
      ))}
      <circle className="lesson" cx={150} cy={222} r={26} />
      <text x={150} y={223}>{lesson}</text>
      <text className="cap" x={150} y={262}>главный урок</text>
    </svg>
  );
}
